import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import CheckboxSquare from '@hero/ui-kit/graphics/CheckboxSquare';
import MembersSparklingIllustration from '@hero/ui-kit/graphics/Illustrations/MembersSparklingIllustration';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import LimitItems from '../../../components/LimitItems';
import Link from '../../../components/Link';
import Error from '../../../components/States/Error';
import NoResults from '../../../components/States/NoResults';
import Table from '../../../components/Table';
import buildHeaderColumns from '../../../components/SortBy/buildHeaderColumns';
import MembersToolbar from './MembersToolbar';
import { useUserStatusContext } from '../../../context/UserStatus';
import { OrganizationMemberListPaginated } from '../types';
import BulkActions from './BulkActions';
import BulkAssignCsvModal from './BulkAssignCsvModal';
import ListItem from './ListItem';
import useListQueryParams from './useListQueryParams';
import { memberSortDefaultDir } from './utils/memberSort';
import { buildColumnOptions } from '../../../utils/tableColumns';
import {
    MEMBER_COLUMNS,
    MEMBER_COLUMNS_STORAGE_KEY,
    MemberColumnId,
    getMemberGridTemplate
} from './columns';

const OrganizationMemberList: React.FC<{
    isLoading: boolean;
    isError: boolean;
    data?: OrganizationMemberListPaginated;
    isBulkMode: boolean;
    selectedMemberIds: Set<number>;
    setSelectedMemberIds: React.Dispatch<React.SetStateAction<Set<number>>>;
    onExitBulkMode: () => void;
    csvModalControls: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}> = ({
    isLoading,
    isError,
    data,
    isBulkMode,
    selectedMemberIds,
    setSelectedMemberIds,
    onExitBulkMode,
    csvModalControls
}) => {
    const isMinL = useIsMinBreakpoint('l');
    const { permissions } = useUserStatusContext();

    // HWM-23020: the Care Manager column resolves CM names via the CM list, which is only
    // fetched for roles holding ENT_CARE_MANAGER_VIEW. Drop the column entirely for roles
    // without it (Hero Standard / Read Only) so they don't get a permanently-"N/A" column.
    const memberColumns = React.useMemo(
        () =>
            permissions.careManager.careManagerView
                ? MEMBER_COLUMNS
                : MEMBER_COLUMNS.filter((column) => column.id !== 'careManager'),
        [permissions.careManager.careManagerView]
    );

    const { isVisible, toggle } = useColumnVisibility(MEMBER_COLUMNS_STORAGE_KEY, memberColumns);

    const { query } = useListQueryParams();
    const { limit, offset } = query;
    const search = query?.search || '';
    const hasFilters = !!query.device_status || !!query.is_online;

    const handleToggleSelect = React.useCallback((memberId: number) => {
        setSelectedMemberIds((prev) => {
            const next = new Set(prev);
            if (next.has(memberId)) {
                next.delete(memberId);
            } else {
                next.add(memberId);
            }
            return next;
        });
    }, []);

    const selectableMemberIds = React.useMemo(() => {
        return (data?.list ?? [])
            .filter((item) => !!item.member_id)
            .map((item) => item.member_id as number);
    }, [data]);

    const allSelected =
        selectableMemberIds.length > 0 &&
        selectableMemberIds.every((id) => selectedMemberIds.has(id));

    const handleToggleSelectAll = React.useCallback(() => {
        setSelectedMemberIds((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                selectableMemberIds.forEach((id) => next.delete(id));
            } else {
                selectableMemberIds.forEach((id) => next.add(id));
            }
            return next;
        });
    }, [allSelected, selectableMemberIds]);

    const headerColumns = React.useMemo(() => {
        const columns = buildHeaderColumns(memberColumns, isVisible, memberSortDefaultDir);

        if (isBulkMode) {
            const selectAllCheckbox = (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        marginLeft: 'calc(var(--gutter) / -2)'
                    }}
                    onClick={handleToggleSelectAll}
                    onKeyDown={() => null}
                    role="button"
                    tabIndex={0}
                >
                    <CheckboxSquare
                        isChecked={allSelected}
                        isHovered={false}
                        isFocused={false}
                        disabled={false}
                        styles={{ width: '2rem', height: '2rem', minWidth: '2rem' }}
                    />
                </div>
            );
            return [selectAllCheckbox, ...columns];
        }

        return columns;
    }, [isBulkMode, allSelected, handleToggleSelectAll, isVisible, memberColumns]);

    const gridTemplate = React.useMemo(
        () => getMemberGridTemplate(memberColumns, isVisible, isBulkMode, isMinL),
        [memberColumns, isVisible, isBulkMode, isMinL]
    );

    const columnControl = React.useMemo(
        () => ({
            options: buildColumnOptions(memberColumns, isVisible),
            onToggle: (id: string) => toggle(id as MemberColumnId)
        }),
        [memberColumns, isVisible, toggle]
    );

    if (isError) {
        return <Error />;
    }

    return (
        <>
            {isBulkMode && (
                <BulkActions
                    selectedMemberIds={selectedMemberIds}
                    totalCount={data?.total_count}
                    onCancel={onExitBulkMode}
                    onSuccess={onExitBulkMode}
                />
            )}

            <BulkAssignCsvModal externalControls={csvModalControls} />

            <MembersToolbar>
                <LimitItems
                    limit={limit ? +limit : 10}
                    offset={offset ? +offset : 0}
                    total_count={data?.total_count}
                    isLoading={isLoading}
                    columnControl={columnControl}
                />
            </MembersToolbar>

            {data?.list.length === 0 && search.length === 0 && !hasFilters ? (
                <Section centered>
                    <H role="h6" styles={{ fontSize: '24px' }}>
                        No members enrolled yet
                    </H>
                    <P>
                        Check out the latest state of potential members via
                        <Link to={'/form-fills'}> Form fill Management</Link>
                    </P>
                    <MembersSparklingIllustration
                        styles={{ height: '20rem', width: '64rem', marginTop: '5rem' }}
                    />
                </Section>
            ) : null}

            {data?.list.length === 0 && search.length ? <NoResults search={search} /> : null}

            {data?.list.length ? (
                <Table
                    pagerData={data}
                    headerColumns={headerColumns}
                    headerGridTemplateColumns={gridTemplate}
                    loading={isLoading}
                    gridColumnGap="small"
                >
                    {data?.list.map((item, index) => (
                        <ListItem
                            externalUserId={item.external_user_id}
                            activatedAt={item.activated_at}
                            memberId={item.member_id}
                            heroUserId={item.hero_user_id}
                            key={`${index}-${item.hero_user_id || item.member_id}-${
                                item.email || 'n/a'
                            }`}
                            name={
                                item.first_name || item.last_name
                                    ? `${item.first_name} ${item.last_name}`
                                    : 'N/A'
                            }
                            firstName={item.first_name}
                            lastName={item.last_name}
                            dateOfBirth={item.date_of_birth}
                            deviceFirstName={item.fre_first_name}
                            deviceLastName={item.fre_last_name}
                            deviceDateOfBirth={item.fre_dob}
                            email={item.email || undefined}
                            deviceInfo={item?.device_info || undefined}
                            exchangeDeviceInfo={item.exchange_device_info || undefined}
                            careManagerIds={item?.care_manager_ids || null}
                            caregivers={item?.caregivers || []}
                            cmMonthlyCheckin={item.cm_monthly_checkin}
                            search={search}
                            role={item.member_role}
                            memberStatus={item.member_status}
                            canOpenDetails={permissions.fleet.fleetMemberView}
                            isBulkMode={isBulkMode}
                            isSelected={!!item.member_id && selectedMemberIds.has(item.member_id)}
                            onToggleSelect={handleToggleSelect}
                            listQuery={query}
                            isColumnVisible={isVisible}
                            gridColumns={gridTemplate}
                        />
                    ))}
                </Table>
            ) : null}
        </>
    );
};

export default OrganizationMemberList;
